import { watchEffect } from "vue";
import { useAccordion } from "@sazka/design-system/src/accordion/composables/use-accordion";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { useGsap, MorphSVGPlugin } from "@sazka/utils/gsap-register";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { MainMenuAnalytics } from "./main-menu-analytics";
import { HeaderOptions, useHeader } from "../header/header";

const { setHeaderState, getHeaderState } = useHeader();
const { init } = useAccordion({
    headerClassName: "main-menu-accordion__button",
    bodyClassName: "main-menu-accordion__nav",
    openOnlyOne: true,
    withScrollIntoView: true,
});
const isSm = useBreakpoints("sm");
const { gsap } = useGsap(MorphSVGPlugin);

const CLOSE_AFTER_MS = 300;
const CLOSING_ANIMATION_MS = 250;
const IS_OPEN_CLASS = "main-menu--open";
const NAV_LIST_NESTED_CLASS = "main-menu-nav-list--nested";
const NAV_ITEM_CLASS = "main-menu-nav-list__item";
const NAV_ITEM_HOVERED_CLASS = NAV_ITEM_CLASS + "--hovered";
const BACK_BUTTON_CLASS = "main-menu-nav-list__back-button";
const SIDEBAR_WRAPPER_CLASS = "main-menu-sidebar__wrapper";
const SVG_PATHS = {
    MENU: "M3 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Z",
    CLOSE: "M3.293 3.293a1 1 0 0 1 1.414 0L12 10.586l7.293-7.293a1 1 0 1 1 1.414 1.414L13.414 12l7.293 7.293a1 1 0 0 1-1.414 1.414L12 13.414l-7.293 7.293a1 1 0 0 1-1.414-1.414L10.586 12 3.293 4.707a1 1 0 0 1 0-1.414Z",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MENU_MOBILE:
        "M2.002 4.519a.5.5 0 0 1 .5-.5h10.996a.5.5 0 1 1 0 1H2.502a.5.5 0 0 1-.5-.5Zm0 4.023a.5.5 0 0 1 .5-.5h10.996a.5.5 0 1 1 0 1H2.502a.5.5 0 0 1-.5-.5Zm0 4.023a.5.5 0 0 1 .5-.5h10.996a.5.5 0 1 1 0 1H2.502a.5.5 0 0 1-.5-.5Z",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CLOSE_MOBILE:
        "M2.146 2.147a.5.5 0 0 1 .707 0L8 7.294l5.146-5.147a.5.5 0 1 1 .707.707L8.707 8.001l5.146 5.146a.5.5 0 1 1-.707.707L8 8.708l-5.147 5.146a.5.5 0 0 1-.707-.707l5.147-5.146-5.147-5.147a.5.5 0 0 1 0-.707Z",
};

const headerElement = document.getElementById("header") as HTMLElement;
const mainMenuElement = document.getElementById("main-menu") as HTMLElement;
const menuToggleButton = document.getElementById("main-menu-toggle") as HTMLButtonElement;
const sidebarMenu = document.getElementById("main-menu-sidebar") as HTMLElement;
const menuIconPath = document.getElementById("main-menu-path");
const menuSvg = document.getElementById("main-menu-svg");

let isMenuOpen = false;
let closeTimer: ReturnType<typeof setTimeout>;
let hideSidebarTimer: ReturnType<typeof setTimeout>;
let wasMenuTransparent = false;

function disablePageScroll() {
    document.body.classList.add("is-menu-open");
}

function enablePageScroll() {
    document.body.classList.remove("is-menu-open");
}

function resetHeaderState() {
    const nextHeaderState: HeaderOptions = {
        locked: false,
        animated: true,
    };
    if (document.documentElement.scrollTop === 0 && wasMenuTransparent) {
        nextHeaderState.transparent = true;
        nextHeaderState.active = false;
        nextHeaderState.parallax = true;
    }
    setHeaderState(nextHeaderState);
}

function toggleMenuOpened(state: boolean) {
    if (state === isMenuOpen) return;

    isMenuOpen = state;

    // Clear any pending timers
    clearTimeout(hideSidebarTimer);

    animateMenuIcon(isMenuOpen);

    if (isMenuOpen) {
        // opening sequence
        wasMenuTransparent = getHeaderState().transparent;
        menuToggleButton.setAttribute("aria-label", "Zavřít hlavní menu");

        document.addEventListener("click", handleClickOutside);
        sidebarMenu.classList.remove("hidden");
        requestAnimationFrame(() => {
            mainMenuElement.classList.add(IS_OPEN_CLASS);
        });
        requestAnimationFrame(() => {
            setHeaderState({
                active: true,
                parallax: false,
                transparent: false,
                animated: false,
                locked: true,
            });
        });

        disablePageScroll();
        MainMenuAnalytics.pushMenuOpened();
    } else {
        // closing sequence
        menuToggleButton.setAttribute("aria-label", "Otevřít hlavní menu");
        document.removeEventListener("click", handleClickOutside);
        mainMenuElement.classList.remove(IS_OPEN_CLASS);
        hideSidebarTimer = setTimeout(() => {
            sidebarMenu.classList.add("hidden");
        }, CLOSING_ANIMATION_MS);

        resetHeaderState();
        enablePageScroll();
    }
}

function getMenuPath() {
    return !isSm.value ? SVG_PATHS.MENU_MOBILE : SVG_PATHS.MENU;
}

function getClosePath() {
    return !isSm.value ? SVG_PATHS.CLOSE_MOBILE : SVG_PATHS.CLOSE;
}

function animateMenuIcon(isOpen: boolean) {
    gsap.to(menuIconPath, {
        duration: 0.25,
        ease: "power2.inOut",
        morphSVG: isOpen ? getClosePath() : getMenuPath(),
    });
}

function handleHamburgerClick() {
    toggleMenuOpened(!isMenuOpen);
}

function closeMenu() {
    toggleMenuOpened(false);
}

function handleClickOutside(event: Event) {
    const target = event.target as HTMLElement;

    if (target.closest(`.main-menu-sidebar__wrapper, .${NAV_LIST_NESTED_CLASS}, .main-menu__button`)) {
        // do not close when clicked inside the menu
        return;
    }

    closeMenu();
}

function initCloseOptions() {
    if (ResponsiveUtils.isMobile()) return;

    // close on ESC keydown
    headerElement.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && isMenuOpen) {
            closeMenu();
            menuToggleButton.focus();
        }
    });

    // close on mouse leave
    mainMenuElement.addEventListener("mouseleave", () => {
        closeTimer = setTimeout(() => {
            closeMenu();
        }, CLOSE_AFTER_MS);
    });

    mainMenuElement.addEventListener("mouseenter", () => {
        clearTimeout(closeTimer);
    });
}

function toggleNestedMenu(element: HTMLElement, value: boolean) {
    const navItem = element.closest(`.${NAV_ITEM_CLASS}`);

    if (!navItem) return;

    navItem.classList.toggle(NAV_ITEM_HOVERED_CLASS, value);

    // When opening a nested menu, ensure the nested panel is scrolled to top
    // so the back button remains visible even if the parent list was scrolled.
    if (value) {
        const nestedMenuEl = navItem.querySelector<HTMLElement>(`.${NAV_LIST_NESTED_CLASS}`);
        const sidebarWrapperEl = navItem.closest<HTMLElement>(`.${SIDEBAR_WRAPPER_CLASS}`);

        if (sidebarWrapperEl) {
            sidebarWrapperEl.scrollTop = 0;
            sidebarWrapperEl.scrollTo?.({ top: 0 });
        }

        if (nestedMenuEl) {
            nestedMenuEl.scrollTop = 0;
            nestedMenuEl.scrollTo?.({ top: 0 });
        }

        setTimeout(() => {
            (navItem.getElementsByClassName("main-menu-link")[1] as HTMLElement).focus();
        }, CLOSING_ANIMATION_MS + 50);
    }
}

function initNestedMenus() {
    const buttons = Array.from(mainMenuElement.querySelectorAll<HTMLElement>(".main-menu__link-expand-button"));
    for (const button of buttons) {
        button.addEventListener("click", (event) => {
            event.stopPropagation();
            const navItem = button.closest(`.${NAV_ITEM_CLASS}`) as HTMLElement;
            toggleNestedMenu(button, !navItem.classList.contains(NAV_ITEM_HOVERED_CLASS));
        });
    }

    const nestedMenus = Array.from(mainMenuElement.querySelectorAll<HTMLElement>(`.${NAV_LIST_NESTED_CLASS}`));
    for (const nestedMenu of nestedMenus) {
        nestedMenu.addEventListener("focusout", (event: FocusEvent) => {
            const currentTarget = event.currentTarget as HTMLElement;
            const focusTarget = event.relatedTarget as Node;
            if (!currentTarget.contains(focusTarget)) {
                toggleNestedMenu(currentTarget, false);
            }
        });
    }

    if (!isSm.value) {
        const backButtons = Array.from(mainMenuElement.querySelectorAll<HTMLElement>(`.${BACK_BUTTON_CLASS}`));
        for (const backButton of backButtons) {
            backButton.addEventListener("click", () => {
                const listItem = backButton.closest(`.${NAV_ITEM_CLASS}`);
                if (listItem) {
                    listItem.classList.remove(NAV_ITEM_HOVERED_CLASS);
                }
            });
        }
    }
}

function initAccordions() {
    const accordions = document.querySelectorAll(".main-menu-accordion__item");
    init(accordions);
}

function initAnalytics() {
    MainMenuAnalytics.initializeMainMenuClickTracking(mainMenuElement);
}

export const useMainMenu = () => {
    if (!menuToggleButton) return;

    menuToggleButton.addEventListener("click", handleHamburgerClick);
    initCloseOptions();
    initNestedMenus();
    initAccordions();
    initAnalytics();

    watchEffect(() => {
        const isMobile = !isSm.value;

        if (menuIconPath && menuSvg) {
            menuSvg.setAttribute("width", isMobile ? "16" : "24");
            menuSvg.setAttribute("height", isMobile ? "16" : "24");
            menuSvg.setAttribute("viewBox", isMobile ? "0 0 16 16" : "0 0 24 24");
            menuIconPath.setAttribute("d", isMobile ? SVG_PATHS.MENU_MOBILE : SVG_PATHS.MENU);
        }
    });
};
